import { GetAccount200ResponsePositionsInner } from "../../codegen-api";
import {
  getPositionIdentity,
  getPositionIdentityCandidates,
} from "../../utils/positionIdentity";
import { IPositionMap } from "./usePositionsWSS.types";

export const positionChannel = "positions";

export function getPositionKey(position: GetAccount200ResponsePositionsInner) {
  return (
    getPositionIdentity(position) ||
    position.instrument_name ||
    `${position.instrument_id}-${position.side}`
  );
}

export function transformPositionsToMap(
  positions?: GetAccount200ResponsePositionsInner[]
) {
  const posMap = positions?.reduce(
    (acc, curr) => ({
      ...acc,
      [getPositionKey(curr)]: curr,
    }),
    {} as IPositionMap
  );
  return posMap || {};
}

export function isNormalizedPositionPatch(position: any) {
  return (
    position &&
    (position.amount !== undefined ||
      position.instrument_name !== undefined ||
      position.instrumentName !== undefined)
  );
}

export function normalizePositionIdentity(position: any): any {
  if (!position) return position;
  const side =
    position.side ??
    (position.isLong === true
      ? "buy"
      : position.isLong === false
      ? "sell"
      : undefined);
  const marginMode =
    position.margin_type ??
    (position.marginMode !== undefined
      ? String(position.marginMode).toLowerCase()
      : undefined);

  return {
    ...position,
    position_key: position.position_key ?? position.positionKey ?? position.id,
    positionKey: position.positionKey ?? position.position_key ?? position.id,
    instrument_id: position.instrument_id ?? position.market,
    side,
    margin_type: marginMode,
  };
}

export function getPositionCandidateKeys(position: any) {
  return getPositionIdentityCandidates(normalizePositionIdentity(position));
}

export function getPositionEventAction(data: any) {
  return String(data?.action || data?.sourceAction || "").toLowerCase();
}

export function isClosedPositionAction(action: string) {
  return (
    action === "closed" ||
    action === "close" ||
    action === "position_closed" ||
    action === "liquidated" ||
    action === "liquidate"
  );
}

// A position patch signals a CLOSE when its status is terminal or its size is
// zero — regardless of whether it is a normalized account patch (`amount`) or a
// raw indexer patch (`sizeInTokens`/`sizeInUsd`). Liquidations publish a raw
// indexer patch (status LIQUIDATED, sizeInTokens 0) with no `action`, so this is
// the signal that removes the stale row from the table.
export function isClosedPositionPatch(position: any): boolean {
  if (!position) return false;
  const status = String(position.status ?? "").toUpperCase();
  if (status === "LIQUIDATED" || status === "CLOSED") {
    return true;
  }
  const rawSize = position.sizeInTokens ?? position.sizeInUsd;
  if (rawSize !== undefined && rawSize !== null && Number(rawSize) === 0) {
    return true;
  }
  if (
    position.amount !== undefined &&
    position.amount !== null &&
    Number(position.amount) === 0
  ) {
    return true;
  }
  return false;
}

/**
 * Fields `/account` may correct after the socket went quiet (FE-5 interim).
 * Until the backend versions position rows, REST replaces a stored row
 * whenever one of these differs, not only when TP/SL or margin changed.
 */
export const MATERIAL_POSITION_FIELDS = [
  "amount",
  "avg_entry_price",
  "liquidation_price",
  "isolated_margin",
] as const;

const isBlank = (value: unknown) =>
  value === undefined || value === null || value === "";

/** Equal as numbers when both are numeric ("0.0060" equals "0.006"), else as strings. */
const sameFieldValue = (a: unknown, b: unknown) => {
  if (isBlank(a) || isBlank(b)) return isBlank(a) === isBlank(b);
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return String(a) === String(b);
};

export function hasMaterialPositionChange(
  stored: GetAccount200ResponsePositionsInner | undefined,
  next: GetAccount200ResponsePositionsInner | undefined
): boolean {
  if (!stored || !next) return stored !== next;
  if (
    stored.triggers?.stop_loss?.order_id !== next.triggers?.stop_loss?.order_id ||
    stored.triggers?.take_profit?.order_id !== next.triggers?.take_profit?.order_id
  ) {
    return true;
  }
  return MATERIAL_POSITION_FIELDS.some(
    (field) => !sameFieldValue(stored[field], next[field])
  );
}

export function getMessageAccount(parsed: any) {
  const data = parsed?.data;
  const dataRecord =
    !Array.isArray(data) && data && typeof data === "object" ? data : undefined;
  return (
    parsed?.account?.toLowerCase?.() ||
    dataRecord?.account?.toLowerCase?.() ||
    dataRecord?.position?.account?.toLowerCase?.()
  );
}
